import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, Form, Input, Select, Tag, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { CameraOutlined } from '@ant-design/icons/lib';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { userRoles } from '../../utils/constants/tagColors';
import useEmailPattern from '../../utils/emailPattern';

const { Option } = Select;
const { Dragger } = Upload;

/**
 * Custom hook that handles the fields for the CreateUpdateUser component.
 *
 * @param {string} photo - The photo of the user.
 * @param {boolean} authorized - Indicates if the user is authorized (super admin can modify everything but not perimeter admin).
 * @returns {Object} The fields and their configuration.
 */
const useFields = (photo, authorized) => {
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const { user, dispatchAPI } = useAuthContext();
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const [enums, setEnums] = useState({});
  const [permissionEnums, setPermissionEnums] = useState([]);
  const [fileList, setFileList] = useState();
  const [base64, setBase64] = useState('');

  // This function convert the PDF to base64 format
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => reject(e);
    });

  const draggerProps = {
    onRemove: () => {
      setFileList([]);
      setBase64();
    },
    beforeUpload: async (file) => {
      const fileExtension = file.name.split('.').pop();
      if (
        fileExtension === 'png' ||
        fileExtension === 'PNG' ||
        fileExtension === 'jpg' ||
        fileExtension === 'JPG'
      ) {
        setFileList([file]);
        const base = await fileToBase64(file);
        setBase64(base);
        return false;
      }
      message('Not a PNG or JPG file.');
      return true;
    },
    fileList
  };

  const roles = enums.roles || [];

  const fields = [
    {
      name: ['photo'],
      input: (
        <Dragger disabled={!authorized} {...draggerProps}>
          <p className="ant-upload-drag-icon">
            <CameraOutlined style={{ color: 'var(--textColor)' }} />
          </p>
          <p className="ant-upload-text">{t('files.create.action')}</p>
        </Dragger>
      )
    },
    {
      name: ['_id'],
      hidden: true,
      noStyle: true
    },
    {
      name: ['last_name'],
      rules: [{ required: true }],
      input: !authorized && <Input disabled />
    },
    {
      name: ['first_name'],
      rules: [{ required: true }],
      input: !authorized && <Input disabled />
    },
    {
      name: ['role'],
      required: true,
      input: (
        <Select loading={isFieldsLoading}>
          {(roles && authorized
            ? roles.slice(0, roles.indexOf(user?.role) + 1)
            : roles.slice(0, 2) || []
          ).map((role) => (
            <Option key={role} value={role}>
              <Tag color={userRoles[role.split(':')[1]]}>
                {t(`users.tags.${role.split(':')[1]}`)}
              </Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['email'],
      rules: [{ required: true }, ...useEmailPattern()],
      input: !authorized && <Input disabled />
    },
    {
      name: ['phone_number'],
      label: 'phone_number.number',
      input: (
        <Input.Group compact>
          <Form.Item
            noStyle
            name={['phone_number', 'country_code']}
            initialValue="+33"
          >
            <Select disabled={!authorized} style={{ width: '25%' }}>
              <Option value="+33">+33</Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle name={['phone_number', 'number']}>
            <Input
              disabled={!authorized}
              type="number"
              style={{ width: '75%' }}
            />
          </Form.Item>
        </Input.Group>
      )
    },
    {
      name: ['date_of_birth'],
      input: <DatePicker disabled={!authorized} style={{ width: '100%' }} />
    },
    {
      label: 'address.number',
      name: ['address', 'number'],
      input: <Input type="number" disabled={!authorized} />
    },
    {
      label: 'address.street',
      name: ['address', 'street'],
      input: !authorized && <Input disabled />
    },
    {
      label: 'address.additional',
      name: ['address', 'additional'],
      input: !authorized && <Input disabled />
    },
    {
      label: 'address.postcode',
      name: ['address', 'postal_code'],
      input: <Input disabled={!authorized} type="number" />
    },
    {
      label: 'address.city',
      name: ['address', 'city'],
      input: !authorized && <Input disabled />
    }
  ];

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/users/enums' });
      setEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getPermissionEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', { url: '/permissions' });
      setPermissionEnums(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  const getSelectOptions = useCallback(async () => {
    setIsFieldsLoading(true);
    await getEnums();
    await getPermissionEnums();
    setIsFieldsLoading(false);
  }, []);

  useEffect(() => {
    getSelectOptions();
  }, [getSelectOptions]);

  useEffect(() => {
    setFileList(photo ? [{ name: 'photo.png' }] : []);
  }, [photo]);

  return {
    fields,
    isFieldsLoading,
    base64,
    permissionEnums
  };
};

export default useFields;
